import {
  ActionIcon,
  Avatar,
  Badge,
  Center,
  Group,
  Pagination,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useState } from 'react';
import { PiKey, PiPencil, PiShieldCheck } from 'react-icons/pi';
import { EditUserModal } from '@/components/admin/EditUserModal';
import { ExplicitPermissionsModal } from '@/components/admin/ExplicitPermissionsModal';
import { RoleAssignmentModal } from '@/components/admin/RoleAssignmentModal';
import DateSinceDisplay from '@/components/common/DateSinceDisplay';
import {
  GenericTable,
  GenericTableColumn,
} from '@/components/GenericTable/GenericTable';
import { useAdminUsersFilters } from '@/hooks/useAdminUsersFilters';
import { useAdminUsers } from '@/models/adminUsers';
import { PERMISSIONS, useHasPermission } from '@/models/permissions';
import type { User } from '@/types/admin';

export default function Users() {
  const canWrite = useHasPermission(PERMISSIONS.RBAC_WRITE);
  const { filters, setFilters } = useAdminUsersFilters();
  const { data, isLoading } = useAdminUsers(filters);

  // Modal states
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [roleModalOpened, setRoleModalOpened] = useState(false);
  const [permissionModalOpened, setPermissionModalOpened] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Column definitions
  const columns: GenericTableColumn<User>[] = [
    {
      accessor: 'info',
      title: 'Name',
      sortable: false,
      width: 250,
      render: (user) => (
        <Group gap="sm" wrap="nowrap">
          <Avatar src={user.info.picture} alt={user.info.name} size="sm" />
          <div>
            <Text size="sm" fw={500}>
              {user.info.name}
            </Text>
            <Text size="xs" c="dimmed">
              ID: {user.id}
            </Text>
          </div>
        </Group>
      ),
    },
    {
      accessor: 'email',
      title: 'Email',
      sortable: false,
      width: 250,
      render: (user) => (
        <Text
          size="sm"
          style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
        >
          {user.email}
        </Text>
      ),
    },
    {
      accessor: 'roles',
      title: 'Roles',
      sortable: false,
      width: 250,
      render: (user) => (
        <Group gap="xs">
          {user.roles && user.roles.length > 0 ? (
            user.roles.map((role) => (
              <Badge key={role.id} size="sm" variant="light">
                {role.name}
              </Badge>
            ))
          ) : (
            <Text size="xs" c="dimmed">
              No roles
            </Text>
          )}
        </Group>
      ),
    },
    {
      accessor: 'createdAt',
      title: 'Created',
      sortable: false,
      width: 150,
      render: (user) => (
        <DateSinceDisplay date={user.createdAt} size="xs" c="dimmed" />
      ),
    },
    {
      accessor: 'id',
      title: 'Actions',
      sortable: false,
      width: 150,
      render: (user) => (
        <Group gap="xs" wrap="nowrap">
          {canWrite && (
            <>
              <Tooltip label="Edit User">
                <ActionIcon
                  variant="subtle"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedUser(user);
                    setEditModalOpened(true);
                  }}
                >
                  <PiPencil size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Manage Roles">
                <ActionIcon
                  variant="subtle"
                  color="blue"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedUser(user);
                    setRoleModalOpened(true);
                  }}
                >
                  <PiShieldCheck size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Manage Permissions">
                <ActionIcon
                  variant="subtle"
                  color="orange"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedUser(user);
                    setPermissionModalOpened(true);
                  }}
                >
                  <PiKey size={16} />
                </ActionIcon>
              </Tooltip>
            </>
          )}
        </Group>
      ),
    },
  ];

  return (
    <Stack gap="md" p="md">
      {/* Header */}
      <Group justify="space-between">
        <Title order={2}>User Management</Title>
      </Group>

      {/* Users Table */}
      <GenericTable<User>
        records={data?.items || []}
        columns={columns}
        loading={isLoading}
        emptyMessage="No users found"
        idAccessor="id"
      />

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <Center>
          <Pagination
            total={data.totalPages}
            value={filters.page || 1}
            onChange={(page) => setFilters({ page })}
            size="sm"
          />
        </Center>
      )}

      {/* Summary */}
      {data && (
        <Text size="xs" c="dimmed">
          Showing {data.items.length} of {data.total} users (Page {data.page} of{' '}
          {data.totalPages})
        </Text>
      )}

      {/* Modals */}
      <EditUserModal
        opened={editModalOpened}
        onClose={() => setEditModalOpened(false)}
        user={selectedUser}
      />
      <RoleAssignmentModal
        opened={roleModalOpened}
        onClose={() => setRoleModalOpened(false)}
        user={selectedUser}
      />
      <ExplicitPermissionsModal
        opened={permissionModalOpened}
        onClose={() => setPermissionModalOpened(false)}
        user={selectedUser}
      />
    </Stack>
  );
}
